import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../bootstrap/Layout";
import Spinner from "../../bootstrap/Spinner";
import { ticketTracking } from "../../redux/action/busAction";

import { SpinnerWrapper } from "./Checkout.styles.js";

const CheckoutPayment = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [paymentMessage, setPaymentMessage] = useState(
    "Please wait while we confirm your payment..."
  );

  const hasRedirected = useRef(false);

  const [passengerInformation, setPassengerInformation] = useState({
    address: "",
    city: "",
    country_id: "",
    first_name: "",
    id_number: "",
    id_type: "",
    last_name: "",
    login_email: "",
    login_mobile: "",
    slug: "",
    status: "",
    user_id: "",
    zip_code: "",
  });

  // Fetch the confirmed booking and redirect to ticket tracking.
  const handleTicketTracking = async (bookingId) => {
    if (hasRedirected.current) return false;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MODULE_DOMAIN}/tickets/bookingid/${encodeURIComponent(
          bookingId
        )}`
      );

      if (!response.ok) {
        throw new Error("Unable to retrieve booking details.");
      }

      const result = await response.json();

      if (result?.status === "success") {
        hasRedirected.current = true;

        dispatch(ticketTracking(result.data));
        toast.success("Payment successful!");
        history.push(
          `/ticket-tracking?booking_id=${encodeURIComponent(bookingId)}`
        );

        return true;
      }

      throw new Error(
        result?.message || "Unable to retrieve your booking details."
      );
    } catch (error) {
      console.error("Ticket tracking error:", error);
      toast.error(error.message || "Unable to retrieve booking details.");
      setLoading(false);
      return false;
    }
  };

  function getParameterByName(name, url = window.location.href) {
    name = name.replace(/[\[\]]/g, "\\$&");

    const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    const results = regex.exec(url);

    if (!results) return null;
    if (!results[2]) return "";

    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  const session = getParameterByName("session");
  const sdata = getParameterByName("data");
  const checkoutRequestId = getParameterByName("checkout_request_id");

  // Existing SSLCommerz payment handling.
  const paymentLatter = async (
    booking_id,
    paydetail,
    paidamount,
    pay_method
  ) => {
    const bookingData = new FormData();

    bookingData.append("booking_id", booking_id);
    bookingData.append("paydetail", paydetail);
    bookingData.append("paidamount", paidamount);
    bookingData.append("pay_method", pay_method);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MODULE_DOMAIN}/tickets/laterpay`,
        {
          method: "POST",
          body: bookingData,
        }
      );

      const result = await response.json();

      if (result?.status === "success") {
        await handleTicketTracking(booking_id);
      } else {
        setLoading(false);
        toast.error(result?.message || "Unable to complete the payment.");
      }

      return result;
    } catch (error) {
      console.error("SSLCommerz payment error:", error);
      toast.error("Unable to complete the payment.");
      setLoading(false);
      return null;
    }
  };

  const getPaid = async (paymentSession, data, bookingId) => {
    try {
      const formdata = new FormData();
      formdata.append("session", paymentSession);
      formdata.append("data", data);

      const paiddata = await fetch(
        `${process.env.REACT_APP_API_MODULE_DOMAIN}/paymethods/sslcommerz/validate`,
        {
          method: "POST",
          body: formdata,
        }
      );

      const result = await paiddata.json();

      if (
        result?.status === "success" &&
        ["VALIDATED", "VALID"].includes(result?.data?.status)
      ) {
        setPaymentMessage("Payment validated. Loading your booking...");

        await paymentLatter(
          bookingId,
          "sslcommerz",
          result.data.amount,
          "5"
        );
      } else {
        setLoading(false);

        const message =
          result?.message ||
          result?.data?.message ||
          "SSLCommerz payment could not be verified.";

        toast.error(message);
        setPaymentMessage(message);
      }
    } catch (error) {
      console.error("SSLCommerz validation error:", error);
      setLoading(false);
      setPaymentMessage("Unable to verify your SSLCommerz payment.");
      toast.error("Unable to verify your payment.");
    }
  };

  // M-PESA: poll the read-only status endpoint.
  const checkMpesaPayment = async (signal) => {
    const statusUrl = `${
      process.env.REACT_APP_API_MODULE_DOMAIN
    }/tickets/mpesa/status/${encodeURIComponent(checkoutRequestId)}`;

    const pollingInterval = 3000;
    const maximumAttempts = 40; // Approximately 2 minutes.

    setLoading(true);
    setPaymentMessage(
      "M-PESA request sent. Please check your phone and enter your PIN."
    );

    for (let attempt = 0; attempt < maximumAttempts; attempt++) {
      if (signal.aborted) return;

      try {
        const response = await fetch(statusUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal,
        });

        const result = await response.json();

        if (signal.aborted) return;

        const paymentStatus = String(result?.status || "")
          .trim()
          .toUpperCase();

        if (paymentStatus === "SUCCESS") {
          // Only redirect after the backend callback has confirmed payment.
          const confirmedBookingId = String(
            result?.booking_id || ""
          ).trim();

          if (!confirmedBookingId) {
            throw new Error(
              "Payment was confirmed, but the booking reference was not returned. Please contact support."
            );
          }

          // Make sure the confirmed payment belongs to this booking.
          if (String(confirmedBookingId) !== String(id)) {
            throw new Error(
              "The confirmed payment does not match this booking reference. Please contact support."
            );
          }

          setPaymentMessage("Payment confirmed. Loading your ticket...");

          await handleTicketTracking(confirmedBookingId);
          return;
        }

        if (
          paymentStatus === "FAILED" ||
          paymentStatus === "CANCELLED"
        ) {
          const message =
            result?.message ||
            (paymentStatus === "CANCELLED"
              ? "M-PESA payment was cancelled."
              : "M-PESA payment failed.");

          setPaymentMessage(message);
          setLoading(false);
          toast.error(message);
          return;
        }

        if (paymentStatus === "NOT_FOUND") {
          setPaymentMessage(
            result?.message ||
              "Payment request is not yet available. Please wait."
          );
        } else if (
          paymentStatus === "PENDING" ||
          paymentStatus === ""
        ) {
          setPaymentMessage(
            "Waiting for M-PESA payment confirmation. Please do not submit another payment."
          );
        } else if (paymentStatus === "ERROR") {
          throw new Error(
            result?.message || "Unable to check payment status."
          );
        }
      } catch (error) {
        if (signal.aborted || error.name === "AbortError") return;

        console.error("M-PESA status check error:", error);

        // A temporary network/server error should not be treated as
        // a failed payment. Continue polling.
        setPaymentMessage(
          "Unable to check payment status right now. Retrying the status check..."
        );
      }

      if (attempt < maximumAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, pollingInterval));
      }
    }

    if (!signal.aborted) {
      setLoading(false);
      setPaymentMessage(
        "Your payment is still being processed. Do not make another payment. You can check your booking status again later or contact support."
      );
      toast.info(
        "Payment confirmation is taking longer than expected. Please do not pay again."
      );
    }
  };

  // Choose the payment flow based on the query parameters.
  useEffect(() => {
    const controller = new AbortController();

    if (checkoutRequestId) {
      checkMpesaPayment(controller.signal);
    } else if (session && sdata) {
      getPaid(session, sdata, id);
    } else {
      setLoading(false);
      setPaymentMessage("No payment confirmation details were provided.");
    }

    return () => {
      controller.abort();
    };
    // This effect should run once for this checkout page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "passengerInformation",
      JSON.stringify(passengerInformation)
    );
  }, [passengerInformation]);

  return (
    <Layout
      title="Checkout"
      description="This is checkout page"
      userProfileInfo={passengerInformation}
    >
      {loading && (
        <SpinnerWrapper>
          <Spinner />
          <p>{paymentMessage}</p>
        </SpinnerWrapper>
      )}

      {!loading && (
        <SpinnerWrapper>
          <p>{paymentMessage}</p>
        </SpinnerWrapper>
      )}
    </Layout>
  );
};

export default CheckoutPayment;